import {getUrlWithoutDomain} from '@aboutyou/utils';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import Stories, {WithSeeMore as WithSeeMoreOriginal} from 'react-insta-stories';
import type {Action, Story} from 'react-insta-stories/dist/interfaces';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {tabletAndUp} from '../../../constants/customMedia';
import {FONT_SIZES} from '../../../constants/typography';
import {useExpensiveValueByViewport} from '../../../hooks/useExpensiveMedia';
import {useTimedOut} from '../../../hooks/useManualTimeout/useManualTimeout';
import {ImageType} from '../../../types/Image';
import {Button as BaseButton} from '../../atoms/Buttons/Button';
import {Image} from '../../atoms/Image/Image';
import {VideoType} from '../ContentBlocks/ContentBlockVideo/ContentBlockVideo';
import {ContentBlockVideoAutoPlay} from '../ContentBlocks/ContentBlockVideoAutoPlay/ContentBlockVideoAutoPlay';

const WithSeeMore = WithSeeMoreOriginal as React.FC<
  React.PropsWithChildren<{
    story: Story;
    action: Action;
  }>
>;

type CTA = {
  link: {
    url: string;
    external: boolean;
  };
  text: string;
  color?: string;
  CtaTrackedComponent: React.FC<React.PropsWithChildren>;
};

type StorySlideType = {
  cta?: CTA;
  durationInMilliseconds?: number;
} & ({image: ImageType} | {video: VideoType});

type StoriesSlideProps = {
  className?: string;
  isActive?: boolean;
  slides: Array<StorySlideType>;
  onStoryEnd?: () => void;
  intersectionThreshold?: number;
  onSlideStart?: (slideIndex: number) => void;
};

const StoriesSlideComponent = ({
  className,
  slides,
  isActive,
  onStoryEnd,
  intersectionThreshold = 0.5,
  onSlideStart,
  ref,
}: StoriesSlideProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const [currentId, setCurrentId] = useState(0);
  const maxHeight = useExpensiveValueByViewport(
    {
      small: '100%',
      medium: '50%',
      large: '85%',
      extraLarge: '100%',
    },
    '100%',
  );

  const reportedStartSlides = useRef(new Set<number>());

  // When the slide goes out out view and comes back in, reset the slide to the first one
  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
    setCurrentId(0);

    if (!isActive) {
      reportedStartSlides.current.clear();
    }
  }, [isActive]);

  const handleOnStoryStart = useCallback(
    (index: number) => {
      if (reportedStartSlides.current.has(index)) return;

      reportedStartSlides.current.add(index);
      onSlideStart?.(index);
    },
    [onSlideStart],
  );

  const handleStoryEnd = useCallback(() => {
    if (currentId < slides.length - 1) {
      setCurrentId(currentId + 1);
      return;
    }
    if (isActive) {
      onStoryEnd?.();
    }
  }, [currentId, isActive, onStoryEnd, slides.length]);

  // we just show the first image/fallbackImage if the slide is not active
  const firstSlide = slides[0]!;
  const firstSlideImage =
    'image' in firstSlide ? firstSlide.image : firstSlide.video.fallbackImage;

  const handleNavigationClick = (direction: 'next' | 'prev') => () => {
    if (direction === 'next') {
      if (currentId === slides.length - 1) {
        onStoryEnd?.();
        return;
      }
      setCurrentId(currentId + 1);
    } else {
      if (currentId === 0) {
        return;
      }
      setCurrentId(currentId - 1);
    }
  };

  return (
    <SlideContainer
      ref={ref}
      className={className}
      data-testid="StoriesSlide"
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
      }}
      data-active={isActive}
    >
      {isActive ? (
        <Stories
          preventDefault={true}
          loop={false}
          currentIndex={currentId}
          width={'100%'}
          height={'100%'}
          storyContainerStyles={{
            borderRadius: '8px',
            backgroundColor: 'transparent',
            maxHeight,
          }}
          onStoryStart={handleOnStoryStart}
          keyboardNavigation={false}
          progressStyles={{
            backgroundColor: COLORS['Fill.White'],
          }}
          progressWrapperStyles={{
            backgroundColor: 'rgba(255, 255, 255, 0.32)',
          }}
          stories={slides.map(slide => {
            return {
              duration: 999999999999,
              content: ({action, story}) => {
                return (
                  <WithSeeMore story={story} action={action}>
                    <DurationTimeoutManager
                      duration={slide.durationInMilliseconds ?? 5000}
                      onTimeout={handleStoryEnd}
                    />
                    <NavigationOverlay
                      align="start"
                      onClick={handleNavigationClick('prev')}
                    />
                    <SlideWrapper>
                      {'image' in slide ? (
                        <StyledImage
                          src={slide.image.url}
                          alt={slide.image.description}
                          widths={[600, 700, 800]}
                          breakpoints={[]}
                          ratio="3:5"
                          importance={isActive ? 'high' : 'low'}
                          loading={isActive ? 'eager' : 'lazy'}
                        />
                      ) : (
                        <StyledVideo
                          video={slide.video}
                          intersectionThreshold={intersectionThreshold}
                          customControlsPosition={'top'}
                          disablePlayPauseControl={true}
                        />
                      )}
                    </SlideWrapper>
                    <NavigationOverlay
                      align="end"
                      onClick={handleNavigationClick('next')}
                    />
                  </WithSeeMore>
                );
              },
              seeMoreCollapsed: () => {
                if (!slide.cta) {
                  return null;
                }

                return <ContentCTA {...slide.cta} />;
              },
              seeMore: () => <></>,
            };
          })}
        />
      ) : (
        <SlideWrapper $maxHeight={maxHeight}>
          <StyledImage
            src={firstSlideImage.url}
            alt={firstSlideImage.description}
            widths={[600, 700, 800]}
            breakpoints={[]}
            ratio="3:5"
            importance={isActive ? 'high' : 'low'}
            loading={isActive ? 'eager' : 'lazy'}
          />
        </SlideWrapper>
      )}
    </SlideContainer>
  );
};

export const StoriesSlide = React.memo(StoriesSlideComponent);

const DurationTimeoutManager: React.FC<{
  duration: number;
  onTimeout: () => void;
}> = ({duration, onTimeout}) => {
  const hasTimedOut = useTimedOut(duration);
  useEffect(() => {
    if (hasTimedOut) {
      onTimeout();
    }
  }, [hasTimedOut, onTimeout]);

  return null;
};

const NavigationOverlay = styled.div<{align: 'start' | 'end'}>`
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  height: 70%;
  width: 50%;
  z-index: 1;

  ${props =>
    props.align === 'start'
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `};
`;

const SlideContainer = styled.div`
  position: relative;
  display: flex;
  transition: transform 0.3s ease-in-out;
  height: 100%;

  width: 100vw;
  ${tabletAndUp(css`
    width: 100%;
  `)}
`;

const SlideWrapper = styled.div<{$maxHeight?: string}>`
  width: 100%;
  height: 100%;

  ${tabletAndUp(css`
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
  `)}

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    background: linear-gradient(
      180deg,
      rgb(0 0 0 / 65%) 0%,
      rgb(0 0 0 / 0%) 100%
    );
    height: 90px;
    ${tabletAndUp(css`
      border-radius: 8px 8px 0 0;
    `)}
  }
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      0deg,
      rgb(0 0 0 / 65%) 0%,
      rgb(0 0 0 / 0%) 100%
    );
    height: 200px;
    ${tabletAndUp(css`
      border-radius: 0 0 8px 8px;
    `)}
  }

  ${({$maxHeight}) =>
    $maxHeight &&
    css`
      max-height: ${$maxHeight};
    `}
`;

const StyledImage = styled(Image)`
  object-fit: cover;
  height: 100%;

  ${tabletAndUp(css`
    border-radius: 8px;
  `)}
`;

const StyledVideo = styled(ContentBlockVideoAutoPlay)`
  height: 100%;
  ${tabletAndUp(css`
    border-radius: 8px;
    overflow: hidden;
  `)}
`;

const Button = styled(BaseButton)<{$textColor?: string}>`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;

  &&&& {
    margin: auto;
    max-width: 200px;
    font-size: ${FONT_SIZES.px18};
  }

  top: -120px;

  ${({$textColor}) =>
    $textColor &&
    css`
      color: ${$textColor} !important;
    `}
`;
const ContentCTA: React.FC<CTA> = ({
  link,
  text,
  color,
  CtaTrackedComponent,
}) => {
  return (
    <CtaTrackedComponent>
      <Button
        href={getUrlWithoutDomain(link.url)}
        variant="glassy"
        $textColor={color}
        size="lg"
      >
        {text}
      </Button>
    </CtaTrackedComponent>
  );
};
